import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const NavigationContext = createContext(null);

const SCOPE_STORAGE_KEY = 'etch.knowledgeScope';
const VALID_SCOPES = new Set(['company', 'all']);

function readStoredScope() {
  try {
    const stored = localStorage.getItem(SCOPE_STORAGE_KEY);
    if (VALID_SCOPES.has(stored)) return stored;
  } catch {
    // Private mode / SSR — fall through to the default.
  }
  // "Firmenwissen" is the default: Etch leads with verifiable company
  // knowledge (spec §11.2), the user opts into general model knowledge.
  return 'company';
}

/**
 * Holds cross-view navigation intent so views can pass data
 * (like file paths, tab selections) without URL query params,
 * plus the app-frame level knowledge scope shown in the shell
 * (spec §8.4 — "obere Leiste mit Menü, Etch-Marke und Wissensquelle").
 *
 * Consumers call consumeIntent() to read & clear in one step.
 */
export function NavigationProvider({ children }) {
  const [intent, setIntent] = useState(null);
  const [knowledgeScope, setKnowledgeScopeState] = useState(readStoredScope);

  const navigate = useCallback((path, data) => {
    setIntent({ path, data });
  }, []);

  const consumeIntent = useCallback(() => {
    const current = intent;
    if (current) setIntent(null);
    return current;
  }, [intent]);

  const setKnowledgeScope = useCallback((next) => {
    setKnowledgeScopeState(VALID_SCOPES.has(next) ? next : 'company');
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SCOPE_STORAGE_KEY, knowledgeScope);
    } catch {
      // Private mode — the scope just won't persist across reloads.
    }
  }, [knowledgeScope]);

  const value = useMemo(
    () => ({ intent, navigate, consumeIntent, knowledgeScope, setKnowledgeScope }),
    [intent, navigate, consumeIntent, knowledgeScope, setKnowledgeScope]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavIntent() {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavIntent must be used within NavigationProvider');
  return ctx;
}

/** Knowledge scope selected in the app frame — `'company' | 'all'`. */
export function useKnowledgeScope() {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useKnowledgeScope must be used within NavigationProvider');
  return { scope: ctx.knowledgeScope, setScope: ctx.setKnowledgeScope };
}

export default NavigationContext;
